import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, Mail, Lock, Chrome } from "lucide-react";

import { debug, info } from "../../utils/logger";

const FormContainer = styled.div`
  max-width: 90%;
  width: 100%;
  margin: 0 auto;
  padding: 1.2rem;
  background: var(--surface-color);
  border-radius: 12px;
  box-shadow: 0 4px 20px var(--shadow-color);
  border: 1px solid var(--border-color);

  @media (min-width: 480px) {
    max-width: 400px;
    padding: 1.5rem;
  }
`;

const Title = styled.h2`
  text-align: center;
  margin-bottom: 1.5rem;
  color: var(--text-color);
  font-size: 1.5rem;

  @media (min-width: 768px) {
    font-size: 1.8rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InputGroup = styled.div`
  position: relative;
  margin-bottom: 1rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px 12px 48px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: var(--surface-color);
  color: var(--text-color);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(77, 171, 247, 0.1);
  }

  &::placeholder {
    color: var(--text-secondary-color);
  }

  @media (max-width: 479px) {
    font-size: 16px; /* Prevent zoom on iOS */
  }
`;

const Icon = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary-color);
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--text-secondary-color);
  cursor: pointer;
  padding: 4px;
  transition: color 0.2s;

  &:hover {
    color: var(--text-color);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 12px;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: var(--primary-color);
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(77, 171, 247, 0.3);
  }

  &:disabled {
    background: var(--secondary-color);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  font-size: 14px;
  text-align: center;
  margin-top: 1rem;
`;

const GoogleButton = styled.button`
  width: 100%;
  padding: 12px 24px;
  background: #4285f4;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover:not(:disabled) {
    background: #3367d6;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(66, 133, 244, 0.3);
  }

  &:disabled {
    background: var(--secondary-color);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  margin: 24px 0;
  color: var(--text-secondary-color);
  font-size: 14px;

  &::before,
  &::after {
    content: "";
    flex: 1;
    border-bottom: 1px solid var(--border-color);
  }

  span {
    padding: 0 16px;
    background: var(--surface-color);
  }
`;

interface LoginFormProps {
  onSwitchToSignup?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSwitchToSignup }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showEmailPassword, setShowEmailPassword] = useState(false);

  const { signIn, signInWithGoogle, error } = useAuth();
  const { t } = useTranslation();

  // Check for admin access - only show email/password if admin mode is enabled
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const adminKey = urlParams.get("admin");
    const isAdminMode = adminKey === process.env.REACT_APP_ADMIN_SECRET;

    if (isAdminMode) {
      setShowEmailPassword(true);
      debug("Admin mode enabled", {
        adminKey: adminKey?.substring(0, 8) + "...",
      });
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;

    setLoading(true);
    try {
      info("Attempting email/password login", { email });
      await signIn(email, password);
      // If successful, the user will be automatically signed in and redirected
    } catch (err: any) {
      console.error("Login failed:", err);
      // Error is already handled in AuthContext, just log it here
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormContainer>
      <Title>{t("auth.welcome")}</Title>

      {/* Google Sign In Button - Primary */}
      <GoogleButton type="button" onClick={signInWithGoogle} disabled={loading}>
        <Chrome size={20} />
        {loading ? t("auth.signingIn") : t("auth.continueWithGoogle")}
      </GoogleButton>

      {/* Email/Password Form - Completely hidden from end users */}
      {showEmailPassword && (
        <>
          {/* Divider for better spacing */}
          <Divider>
            <span>{t("auth.or")}</span>
          </Divider>

          <Form onSubmit={handleSubmit}>
            <InputGroup>
              <Icon>
                <Mail size={20} />
              </Icon>
              <Input
                type="email"
                placeholder={t("auth.email")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </InputGroup>

            <InputGroup>
              <Icon>
                <Lock size={20} />
              </Icon>
              <Input
                type={showPassword ? "text" : "password"}
                placeholder={t("auth.password")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <PasswordToggle
                type="button"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </PasswordToggle>
            </InputGroup>

            <Button type="submit" disabled={loading}>
              {loading ? t("auth.signingIn") : t("auth.signIn")}
            </Button>
          </Form>
        </>
      )}

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {/* Removed signup link since Google auth handles both signin and signup */}
    </FormContainer>
  );
};

export default LoginForm;
